/**
 * Admin adapter for the backend public HTTP error contract.
 *
 * @remarks
 * `docs/backend/readmes/PUBLIC_ERROR_CONTRACT.md` owns the API response shape.
 * This file adapts backend failures into safe operator UI copy. Keep the shared
 * coercion/sanitization rules aligned with
 * `apps/web/src/utils/errors/publicErrorContract.ts`, but do not byte-sync the
 * files: admin can add stricter operator-only filters when needed.
 *
 * @see docs/backend/readmes/PUBLIC_ERROR_CONTRACT.md - public HTTP error shape
 * @see docs/LOGGING.md - no raw internals, secrets, provider payloads, or PII
 * @see docs/guides/PLAIN_LANGUAGE.md - operator-facing copy quality bar
 */
export interface PublicErrorResponse {
  errorCode: string;
  message: string;
  correlationId: string;
  details?: Record<string, unknown>;
}

/**
 * Patterns that are never safe to show as admin error copy.
 *
 * @remarks
 * Admin renders internal tooling and support flows, so this adapter must keep
 * internal route paths and credential-like identifiers out of displayed copy.
 */
export const DEFINITELY_INTERNAL_ERROR_PATTERNS: readonly RegExp[] = [
  // SQL / DB markers
  /select\s+.+\s+from\s+/i,
  /insert\s+into\s+/i,
  /update\s+.+\s+set\s+/i,
  /delete\s+from\s+/i,
  /syntax error at or near/i,
  /sqlstate/i,
  /postgres(?:ql)?/i,
  /relation\s+"[^"]+"/i,
  /column\s+"[^"]+"\s+does not exist/i,
  /public\."[^"]+"/i,
  /knex/i,

  // Stack trace / file path markers
  /\n\s*at\s+[^\n]+:[0-9]+:[0-9]+/i,
  /\bnode_modules\b/i,
  // CRITICAL: PD-286 and docs/backend/readmes/PUBLIC_ERROR_CONTRACT.md prohibit exposing source paths.
  // PD-377 C7 (#5851 review finding 1): module extensions (.mjs/.cjs/.mts/.cts),
  // Windows drive paths (spaces allowed in folders), UNC \\server\share paths
  // and single-segment ./ or ../ paths are source paths too.
  // Keep in sync across apps/web, apps/admin and services/api leakMarkers.ts.
  /\/[A-Za-z0-9._-]+(?:\/[A-Za-z0-9._-]+)+\.(?:[cm]?[jt]sx?|json|sql)\b/i,
  /\b[A-Za-z]:\\(?:[^\\\n]+\\)*[^\\\s]+\.(?:[cm]?[jt]sx?|json|sql)\b/i,
  /\\\\[^\\\s]+\\[^\\\s]+\\(?:[^\\\n]+\\)*[^\\\s]+\.(?:[cm]?[jt]sx?|json|sql)\b/i,
  /(?:^|[\s("'`=])\.{1,2}\/[A-Za-z0-9._-]+\.(?:[cm]?[jt]sx?|json|sql)\b/i,

  // Internal API implementation details and credential-like fragments.
  /\/admin\/[A-Za-z0-9/_-]+/i,
  /token=[^\s)&#]+/i,
  /ERR_CONNECTION_REFUSED/i,
  /\b(?:sub|pi|cus|ch|price|prod)_[A-Za-z0-9]+\b/i,
];

export const DEFAULT_PUBLIC_MESSAGE = "Something went wrong. Please try again.";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value && typeof value === "object" && !Array.isArray(value));

const normalizeString = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

const isSensitiveMessage = (message: string): boolean =>
  DEFINITELY_INTERNAL_ERROR_PATTERNS.some((pattern) => pattern.test(message));

/**
 * Returns display-safe copy, falling back when a message looks internal.
 */
export const sanitizePublicMessage = (message: string): string => {
  const normalized = message.trim();
  if (!normalized) return DEFAULT_PUBLIC_MESSAGE;
  if (normalized.length > 220) return DEFAULT_PUBLIC_MESSAGE;
  if (isSensitiveMessage(normalized)) return DEFAULT_PUBLIC_MESSAGE;
  return normalized;
};

const statusToDefaultErrorCode = (status: number | undefined): string => {
  switch (status) {
    case undefined:
      return "UNKNOWN_ERROR";
    case 400:
      return "VALIDATION_ERROR";
    case 401:
      return "UNAUTHORIZED";
    case 403:
      return "FORBIDDEN";
    case 404:
      return "NOT_FOUND";
    case 409:
      return "CONFLICT";
    case 429:
      return "RATE_LIMITED";
    case 502:
    case 503:
      return "SERVICE_UNAVAILABLE";
    default:
      return "INTERNAL_ERROR";
  }
};

/**
 * Checks whether an unknown value already matches the canonical backend shape.
 */
export const isPublicErrorResponse = (
  value: unknown,
): value is PublicErrorResponse => {
  if (!isRecord(value)) return false;
  return (
    typeof value.errorCode === "string" &&
    typeof value.message === "string" &&
    typeof value.correlationId === "string"
  );
};

interface ProblemDetailsResponse {
  type: string;
  title: string;
  status: number;
  detail: string;
  errorCode: string;
  correlationId: string;
  details?: Record<string, unknown>;
}

const isProblemDetailsResponse = (
  value: unknown,
): value is ProblemDetailsResponse =>
  isRecord(value) &&
  typeof value.type === "string" &&
  typeof value.title === "string" &&
  typeof value.status === "number" &&
  typeof value.detail === "string" &&
  typeof value.errorCode === "string" &&
  typeof value.correlationId === "string" &&
  (!("details" in value) || isRecord(value.details));

const isPartialProblemDetailsLike = (value: Record<string, unknown>): boolean =>
  ["type", "title", "status", "detail"].some((field) => field in value);

const hasNestedMetadataError = (value: Record<string, unknown>): boolean =>
  isRecord(value.error);

/**
 * Coerces canonical backend failures into the frontend-safe shape.
 *
 * @remarks
 * The backend contract is authoritative. The only non-canonical object shape
 * accepted from the backend is the opt-in RFC 9457 Problem Details pilot, which
 * must carry the same `errorCode` and `correlationId` extension members during
 * the compatibility window.
 *
 * Some Admin mutation surfaces still receive message-only RTK payloads. This
 * adapter accepts only the safe top-level `message`, `errorCode`, and
 * `correlationId` fields from that compatibility path, after applying the same
 * public-message sanitizer. Frontend-only metadata DTOs with nested `error`
 * objects remain owned by their feature display code, not this public HTTP
 * adapter. The plain `{ error, code }` mirrors are still ignored here.
 */
export const coercePublicErrorResponse = (args: {
  status?: number;
  data: unknown;
  correlationId?: string | null;
}): PublicErrorResponse => {
  const correlationIdFromArgs = normalizeString(args.correlationId) ?? null;

  if (isPublicErrorResponse(args.data)) {
    const correlationId =
      correlationIdFromArgs ??
      normalizeString(args.data.correlationId) ??
      "unknown";
    return {
      errorCode:
        normalizeString(args.data.errorCode) ??
        statusToDefaultErrorCode(args.status),
      message: sanitizePublicMessage(args.data.message),
      correlationId,
      ...(isRecord(args.data.details) ? { details: args.data.details } : {}),
    };
  }

  if (isRecord(args.data)) {
    const problemDetails = isProblemDetailsResponse(args.data)
      ? args.data
      : null;

    if (problemDetails === null && isPartialProblemDetailsLike(args.data)) {
      return {
        errorCode: statusToDefaultErrorCode(args.status),
        message: DEFAULT_PUBLIC_MESSAGE,
        correlationId: correlationIdFromArgs ?? "unknown",
      };
    }

    const correlationId =
      correlationIdFromArgs ??
      normalizeString(problemDetails?.correlationId) ??
      "unknown";

    if (problemDetails === null && !hasNestedMetadataError(args.data)) {
      const mutationMessage = normalizeString(args.data.message);
      const mutationErrorCode = normalizeString(args.data.errorCode);
      const mutationCorrelationId =
        correlationIdFromArgs ??
        normalizeString(args.data.correlationId) ??
        "unknown";
      if (mutationMessage ?? mutationErrorCode) {
        return {
          errorCode: mutationErrorCode ?? statusToDefaultErrorCode(args.status),
          message: sanitizePublicMessage(
            mutationMessage ?? DEFAULT_PUBLIC_MESSAGE,
          ),
          correlationId: mutationCorrelationId,
        };
      }
    }

    return {
      errorCode:
        normalizeString(problemDetails?.errorCode) ??
        statusToDefaultErrorCode(args.status),
      message: sanitizePublicMessage(
        normalizeString(problemDetails?.detail) ?? DEFAULT_PUBLIC_MESSAGE,
      ),
      correlationId,
      ...(isRecord(problemDetails?.details)
        ? { details: problemDetails.details }
        : {}),
    };
  }

  if (typeof args.data === "string") {
    return {
      errorCode: statusToDefaultErrorCode(args.status),
      message: sanitizePublicMessage(args.data),
      correlationId: correlationIdFromArgs ?? "unknown",
    };
  }

  return {
    errorCode: statusToDefaultErrorCode(args.status),
    message: DEFAULT_PUBLIC_MESSAGE,
    correlationId: correlationIdFromArgs ?? "unknown",
  };
};

/**
 * Formats public error copy with support-friendly correlation metadata.
 */
export const formatPublicErrorForDisplay = (
  publicError: PublicErrorResponse,
): string => {
  const correlationId = normalizeString(publicError.correlationId);
  if (!correlationId || correlationId === "unknown") {
    return publicError.message;
  }
  return `${publicError.message} (Ref: ${correlationId})`;
};
